import React, { useEffect, useMemo } from 'react';
import { useGetGlassValuePatternedOptionsQuery } from '../../store/slices/apiSlice';

export interface PatternedGlassOptionSelectProps {
  glassValueId: string | null | undefined;
  supportsPatterned: boolean;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  rawFallbackLabel?: string | null | undefined;
  label?: string;
  disabled?: boolean;
}

/**
 * Second-step picker for patterned subtype when the parent glass value is configured for it.
 */
export const PatternedGlassOptionSelect: React.FC<PatternedGlassOptionSelectProps> = ({
  glassValueId,
  supportsPatterned,
  value,
  onChange,
  rawFallbackLabel,
  label = 'Patterned glass',
  disabled,
}) => {
  const { data, isFetching } = useGetGlassValuePatternedOptionsQuery(glassValueId!, {
    skip: !glassValueId || !supportsPatterned,
  });

  const options = data?.results ?? [];
  const normalizedRawFallback = useMemo(
    () => (rawFallbackLabel ? rawFallbackLabel.trim().toLowerCase() : ''),
    [rawFallbackLabel],
  );
  const resolvedFromRaw = useMemo(() => {
    if (value || !normalizedRawFallback || options.length === 0) return null;
    const match = options.find((o) => o.label.trim().toLowerCase() === normalizedRawFallback);
    return match?.id ?? null;
  }, [options, normalizedRawFallback, value]);

  useEffect(() => {
    if (!value && resolvedFromRaw) {
      onChange(resolvedFromRaw);
    }
  }, [value, resolvedFromRaw, onChange]);

  if (!glassValueId || !supportsPatterned || options.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      <label className="form-label">{label}</label>
      <select
        className="form-input w-full"
        value={value ?? resolvedFromRaw ?? ''}
        disabled={disabled || isFetching}
        onChange={(e) => onChange(e.target.value ? e.target.value : null)}
      >
        <option value="">Optional pattern</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};
